import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, LineReveal } from "../components/Reveal";
import { EventCard, StoryCard } from "../components/cards";
import { getPillar, getPillarEvents, getPillarStories } from "../lib/content";

export default function PillarPage({ pillarKey }) {
  const pillar = getPillar(pillarKey);
  const events = getPillarEvents(pillarKey).slice(0, 2);
  const stories = getPillarStories(pillarKey).slice(0, 2);

  return (
    <main>
      <SEO
        title={pillar.name}
        description={pillar.shortDescription}
        path={`/${pillarKey}`}
        image={pillar.image}
      />

      <section className="pt-32 md:pt-44 pb-16 md:pb-24" data-testid={`${pillarKey}-hero`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p
              className="text-xs uppercase tracking-[0.25em] font-semibold"
              style={{ color: pillar.accentText }}
            >
              {pillar.eyebrow}
            </p>
            <h1 className="mt-6 font-serif tracking-tighter leading-[0.98] text-ink text-5xl sm:text-6xl lg:text-7xl">
              <LineReveal lines={[pillar.headline]} delay={0.15} />
            </h1>
            <Reveal delay={0.45}>
              <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink-2">
                {pillar.philosophy}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/get-involved"
                  data-testid={`${pillarKey}-cta-involved`}
                  className="rounded-full px-7 py-3.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.04] active:scale-95"
                  style={{ backgroundColor: pillar.accent, color: pillar.onAccent }}
                >
                  Get involved
                </Link>
                <Link
                  to="/activities"
                  data-testid={`${pillarKey}-cta-activities`}
                  className="rounded-full border border-ink px-7 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-ink hover:text-sand"
                >
                  See activities
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 relative">
            <Reveal delay={0.3}>
              <div
                className="overflow-hidden rounded-xl aspect-[4/5] max-w-md ml-auto border-l-8"
                style={{ borderColor: pillar.accent }}
              >
                <img src={pillar.image} alt={pillar.imageAlt} className="h-full w-full object-cover" />
              </div>
              <div className="absolute -bottom-8 -left-2 sm:left-6 w-32 sm:w-40 aspect-square overflow-hidden rounded-xl border-4 border-sand">
                <img
                  src={pillar.secondaryImage}
                  alt={pillar.secondaryImageAlt}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-sand-2 border-y border-line" data-testid={`${pillarKey}-focus`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ink-2">Where the work goes</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
              Areas of focus
            </h2>
            <p className="mt-5 max-w-xl text-sm md:text-base text-ink-2 leading-relaxed">
              Planned areas of work under this pillar — each will be announced
              here as it becomes operational.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillar.focusAreas.map((area, i) => (
              <Reveal key={area.title} delay={i * 0.07}>
                <div className="h-full border border-line rounded-xl bg-sand p-7 md:p-8">
                  <p className="font-serif italic text-lg" style={{ color: pillar.accentText }}>
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-3 font-serif text-2xl tracking-tight text-ink">{area.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-2">{area.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div
              className="mt-10 border border-dashed rounded-xl p-7 md:p-8"
              style={{ borderColor: `${pillar.accent}80` }}
              data-testid={`${pillarKey}-horizon`}
            >
              <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: pillar.accentText }}>
                On the horizon
              </p>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-ink-2">
                {pillar.buildingToward.join(" · ")}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-ink-2 border-t border-line pt-4">
                {pillar.statusNote}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {(events.length > 0 || stories.length > 0) && (
        <section className="py-20 md:py-28" data-testid={`${pillarKey}-related`}>
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">
                From this pillar
              </h2>
            </Reveal>
            {events.length > 0 && (
              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                {events.map((event, i) => (
                  <Reveal key={event.slug} delay={i * 0.1}>
                    <EventCard event={event} past={event.status === "past"} />
                  </Reveal>
                ))}
              </div>
            )}
            {stories.length > 0 && (
              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10">
                {stories.map((story, i) => (
                  <Reveal key={story.slug} delay={i * 0.1}>
                    <StoryCard story={story} withImage />
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="py-20 md:py-28" data-testid={`${pillarKey}-cta`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div
              className="rounded-xl p-10 md:p-16"
              style={{ backgroundColor: pillar.accent, color: pillar.onAccent }}
            >
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight max-w-2xl leading-tight">
                The door is open. Walk through it.
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed" style={{ color: pillar.onAccent, opacity: 0.85 }}>
                Whether it's an hour, an idea or a helping hand — there's a place
                for it here.
              </p>
              <Link
                to="/get-involved"
                data-testid={`${pillarKey}-cta-bottom`}
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-sand text-ink px-7 py-3.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              >
                Get involved
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
